import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Pagination from "./Pagination";
import {
  listAllScreenings,
  apiErrorMessage,
  TRACKER_STATUS_OPTIONS,
  TRACKER_STATUS_LABELS,
  VERDICT_LABELS,
} from "../api/screeningApi";
import { roleLabel } from "../utils/formatters";
import "../css/ClientFilters.css";

// The AI screening tracking table, with its own filter bar and pagination.
//
// What rows come back is decided by the BACKEND from the login cookie:
//   TEAM_LEAD / RECRUITER -> only screenings they scheduled
//   ADMIN                 -> everyone's, plus a "Scheduled by" column + filter
//
// Props:
//   isAdmin        show the "Scheduled by" column + filter
//   users          [{_id, name, role}] for the "Scheduled by" dropdown (admin)
//   scheduledBy    lets a parent (admin team table) pick a person from outside
//   onScheduledByChange  tells that parent when the dropdown changes
//   title          card heading

const EMPTY_FILTERS = {
  search: "",
  status: "",
  recommendation: "",
  scheduledBy: "",
  startDate: "",
  endDate: "",
  sort: "newest",
};
const PAGE_SIZE = 10;

const initials = (name = "") =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase() || "?";

// "3h ago", "in 2 days" — short relative times for the status line.
function relTime(value) {
  if (!value) return "";
  const diff = new Date(value).getTime() - Date.now();
  const abs = Math.abs(diff);
  const units = [
    ["day", 86400000],
    ["hour", 3600000],
    ["minute", 60000],
  ];
  const rtf = new Intl.RelativeTimeFormat(undefined, { numeric: "auto", style: "short" });
  for (const [unit, ms] of units) {
    if (abs >= ms || unit === "minute") return rtf.format(Math.round(diff / ms), unit);
  }
  return "";
}

const fmtDate = (v) => (v ? new Date(v).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : "—");
const fmtTime = (v) => (v ? new Date(v).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" }) : "");

// The grey line under the status badge: what's happening right now.
function progressNote(s) {
  switch (s.displayStatus) {
    case "DRAFT":
      return "Questions not sent yet";
    case "SENT":
      return s.linkOpenedAt ? `Opened ${relTime(s.linkOpenedAt)}` : `Not opened · expires ${relTime(s.expiresAt)}`;
    case "IN_PROGRESS":
      return `${s.answeredCount}/${s.questionCount} answered · expires ${relTime(s.expiresAt)}`;
    case "COMPLETED":
      return `Finished ${relTime(s.completedAt)}`;
    case "REPORT_READY":
    case "REPORT_FAILED":
      return `Finished ${fmtDate(s.completedAt)}`;
    case "EXPIRED":
      return s.linkOpenedAt ? `Opened, not finished · ${s.answeredCount}/${s.questionCount}` : "Never opened";
    case "CANCELLED":
      return "Link withdrawn";
    default:
      return "";
  }
}

function StatusBadge({ status }) {
  return (
    <span className={`st-status st-status-${status.toLowerCase().replace(/_/g, "-")}`}>
      <span className="st-status-dot" aria-hidden="true" />
      {TRACKER_STATUS_LABELS[status] || status}
    </span>
  );
}

function ScoreCell({ score, recommendation }) {
  if (score == null) return <span className="cl-dash">—</span>;
  return (
    <div className="st-score">
      <span className="st-score-num">
        <b>{score}</b>
        <span>/100</span>
      </span>
      {recommendation && (
        <span className={`st-verdict st-verdict-${recommendation}`}>{VERDICT_LABELS[recommendation] || recommendation}</span>
      )}
    </div>
  );
}

export default function ScreeningTracker({ isAdmin = false, users = [], scheduledBy, onScheduledByChange, title }) {
  const [draft, setDraft] = useState(EMPTY_FILTERS);
  const [applied, setApplied] = useState(EMPTY_FILTERS);
  const [page, setPage] = useState(1);

  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [copiedId, setCopiedId] = useState(null);

  const load = useCallback(async (filters, pageNo) => {
    setLoading(true);
    setError("");
    try {
      const res = await listAllScreenings({ ...filters, page: pageNo, limit: PAGE_SIZE });
      setRows(res.data.screenings);
      setPagination(res.data.pagination);
    } catch (err) {
      setError(apiErrorMessage(err, "Couldn't load screenings."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(applied, page);
  }, [load, applied, page]);

  // A parent (admin team table) picked a person: filter the list to them.
  useEffect(() => {
    if (scheduledBy === undefined) return;
    setDraft((d) => (d.scheduledBy === scheduledBy ? d : { ...d, scheduledBy }));
    setApplied((a) => (a.scheduledBy === scheduledBy ? a : { ...a, scheduledBy }));
    setPage(1);
  }, [scheduledBy]);

  const set = (key) => (e) => setDraft((d) => ({ ...d, [key]: e.target.value }));
  const controlClass = (key) => `cf-control${draft[key] && !(key === "sort" && draft[key] === "newest") ? " is-set" : ""}`;
  const hasAnyFilter = Object.keys(EMPTY_FILTERS).some((k) => draft[k] !== EMPTY_FILTERS[k]);

  const apply = () => {
    setPage(1);
    setApplied({ ...draft });
    if (onScheduledByChange && draft.scheduledBy !== applied.scheduledBy) onScheduledByChange(draft.scheduledBy);
  };
  const reset = () => {
    setDraft(EMPTY_FILTERS);
    setApplied(EMPTY_FILTERS);
    setPage(1);
    if (onScheduledByChange) onScheduledByChange("");
  };
  const applyOnEnter = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      apply();
    }
  };

  const copyLink = async (row) => {
    try {
      await navigator.clipboard.writeText(row.link);
      setCopiedId(row._id);
      setTimeout(() => setCopiedId((id) => (id === row._id ? null : id)), 2000);
    } catch {
      window.prompt("Copy the interview link:", row.link);
    }
  };

  const renderTable = () => {
    if (loading && rows.length === 0) return <div className="cl-empty">Loading screenings…</div>;
    if (rows.length === 0) {
      return (
        <div className="cl-empty">
          <strong>No screenings match these filters.</strong>
          <span>
            {hasAnyFilter
              ? "Try clearing the filters above."
              : "Screenings appear here once they're created from an optimization's AI screening panel."}
          </span>
        </div>
      );
    }

    return (
      <>
        <div className={`cl-table-wrap${loading ? " st-is-loading" : ""}`}>
          <table className={`cl-table st-table${isAdmin ? " st-table-admin" : ""}`}>
            <colgroup>
              <col className="st-col-candidate" />
              <col className="st-col-role" />
              {isAdmin && <col className="st-col-by" />}
              <col className="st-col-status" />
              <col className="st-col-score" />
              <col className="st-col-date" />
              <col className="st-col-actions" />
            </colgroup>
            <thead>
              <tr>
                <th>Candidate</th>
                <th>Role</th>
                {isAdmin && <th>Scheduled by</th>}
                <th>Status</th>
                <th>Score</th>
                <th>Scheduled</th>
                <th className="cl-th-actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((s) => (
                <tr key={s._id}>
                  <td>
                    <div className="cl-name">
                      <span className="cl-mono">{initials(s.candidate?.name)}</span>
                      <div className="st-stack-text">
                        <span className="cl-name-text st-ellipsis" title={s.candidate?.name}>
                          {s.candidate?.name || "—"}
                        </span>
                        <span className="st-sub st-ellipsis" title={s.candidate?.email}>
                          {s.candidate?.email || ""}
                        </span>
                      </div>
                    </div>
                  </td>

                  <td>
                    <div className="st-stack-text">
                      <Link to={`/dashboard/optimizations/${s.optimizationId}`} className="st-role st-ellipsis" title={s.jobTitle}>
                        {s.jobTitle || <span className="st-untitled">Untitled role</span>}
                      </Link>
                      <span className="st-sub">
                        {s.questionCount} questions{s.resumeVersionNumber ? ` · resume v${s.resumeVersionNumber}` : ""}
                      </span>
                    </div>
                  </td>

                  {isAdmin && (
                    <td>
                      <div className="st-stack-text">
                        <span className="st-ellipsis" title={s.scheduledBy?.name}>
                          {s.scheduledBy?.name || "—"}
                        </span>
                        <span className="st-sub">{roleLabel(s.scheduledBy?.role)}</span>
                      </div>
                    </td>
                  )}

                  <td>
                    <div className="st-stack-text">
                      <StatusBadge status={s.displayStatus} />
                      <span className="st-sub st-ellipsis">{progressNote(s)}</span>
                    </div>
                  </td>

                  <td>
                    <ScoreCell score={s.overallScore} recommendation={s.recommendation} />
                  </td>

                  <td>
                    <div className="cl-date">
                      <span>{fmtDate(s.createdAt)}</span>
                      <span className="cl-time">{fmtTime(s.createdAt)}</span>
                    </div>
                  </td>

                  <td className="cl-actions">
                    {s.link && (
                      <button
                        type="button"
                        className="cl-btn cl-btn-row"
                        onClick={() => copyLink(s)}
                        title="Copy the candidate's interview link"
                      >
                        {copiedId === s._id ? "Copied ✓" : "Copy link"}
                      </button>
                    )}
                    {s.displayStatus === "DRAFT" ? (
                      <Link to={`/dashboard/optimizations/${s.optimizationId}`} className="cl-btn cl-btn-row">
                        Review
                      </Link>
                    ) : (
                      <Link
                        to={`/dashboard/screenings/${s._id}`}
                        className={`cl-btn cl-btn-row${s.displayStatus === "REPORT_READY" ? " st-btn-strong" : ""}`}
                      >
                        {s.displayStatus === "REPORT_READY" ? "Report" : "View"}
                      </Link>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="cl-pagination">
          <Pagination pagination={pagination} onPageChange={setPage} />
        </div>
      </>
    );
  };

  return (
    <>
      <div className="cl-filters">
        <div className="cf-bar">
          <div className={`cf-grid st-filter-grid${isAdmin ? " is-admin" : ""}`}>
            <div className="cf-field st-field-search">
              <label htmlFor="st-search">Search</label>
              <input
                id="st-search"
                type="search"
                className={controlClass("search")}
                value={draft.search}
                onChange={set("search")}
                onKeyDown={applyOnEnter}
                placeholder="Candidate name, email or job title"
              />
            </div>

            <div className="cf-field">
              <label htmlFor="st-status">Status</label>
              <select id="st-status" className={controlClass("status")} value={draft.status} onChange={set("status")}>
                <option value="">All statuses</option>
                {TRACKER_STATUS_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="cf-field">
              <label htmlFor="st-verdict">AI recommendation</label>
              <select
                id="st-verdict"
                className={controlClass("recommendation")}
                value={draft.recommendation}
                onChange={set("recommendation")}
              >
                <option value="">Any</option>
                {Object.entries(VERDICT_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </div>

            {isAdmin && (
              <div className="cf-field">
                <label htmlFor="st-by">Scheduled by</label>
                <select id="st-by" className={controlClass("scheduledBy")} value={draft.scheduledBy} onChange={set("scheduledBy")}>
                  <option value="">Everyone</option>
                  {users.map((u) => (
                    <option key={u._id} value={u._id}>
                      {u.name} ({roleLabel(u.role)})
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="cf-field">
              <label htmlFor="st-from">Scheduled from</label>
              <input
                id="st-from"
                type="date"
                className={controlClass("startDate")}
                value={draft.startDate}
                max={draft.endDate || undefined}
                onChange={set("startDate")}
              />
            </div>

            <div className="cf-field">
              <label htmlFor="st-to">Scheduled to</label>
              <input
                id="st-to"
                type="date"
                className={controlClass("endDate")}
                value={draft.endDate}
                min={draft.startDate || undefined}
                onChange={set("endDate")}
              />
            </div>

            <div className="cf-field">
              <label htmlFor="st-sort">Sort by</label>
              <select id="st-sort" className={controlClass("sort")} value={draft.sort} onChange={set("sort")}>
                <option value="newest">Newest first</option>
                <option value="oldest">Oldest first</option>
                <option value="score_high">Highest score</option>
                <option value="score_low">Lowest score</option>
              </select>
            </div>

            <div className="cf-actions">
              <button type="button" className="cf-btn cf-btn-primary" onClick={apply} disabled={loading}>
                Apply
              </button>
              <button type="button" className="cf-btn cf-btn-secondary" onClick={reset} disabled={!hasAnyFilter || loading}>
                Reset
              </button>
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="cl-error" role="alert">
          {error}
        </div>
      )}

      <section className="cl-card">
        <div className="cl-card-head st-card-head">
          <h2>{title || (isAdmin ? "All AI screenings" : "Your AI screenings")}</h2>
          {pagination && <span className="st-count">{pagination.total} total</span>}
        </div>
        {renderTable()}
      </section>
    </>
  );
}